import { DeviceMode } from '@vedalib/editor'
import { SectionTypeEnum } from '@vedalib/editor/lib/elements'
import cn from 'classnames'
import { AnimatePresence } from 'framer-motion'
import { useEffect, useRef } from 'react'

import ButtonCourse from 'components/Editor/EditorElements/courseKit/ButtonCourse'
import { CourseKitSize } from 'components/Editor/EditorElements/courseKit/CourseKitTypes'
import Empty from 'components/Editor/EditorElements/courseKit/EmptyCourse'
import DraggablePanel from 'components/Editor/cource/DraggablePanel'
import { useFontCss } from 'services/Branding/hooks'
import { useProjectT } from 'services/ProjectLabels'
import {
  hideIncompleteElementList,
  hideNotCompletedSections,
  setCourseNavigation,
  setElementNavigation,
  setHighlightPreview,
} from 'services/Store/Project/actions'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import {
  getAllElementsByIds,
  getCourseEndButtonElement,
  getCourseState,
  getProgress,
  getSection,
  getSections,
} from 'services/Store/Project/selectors'
import { t } from 'services/Translation'
import { focusFirstFocusableInElement } from 'utils/accessibility'

import * as s from './IncompleteElementModal.module.scss'
import IncompleteElementButton from './styled/IncompleteElementButton'
import IncompleteElementsListStyled from './styled/IncompleteElementsListStyled'

const getElementLabel = (type: string) => {
  return `elements.${type}.label`
}

const IncompleteElementModal = () => {
  const pt = useProjectT()
  const section = useProjectContext(getSection)
  const refRoot = useRef<HTMLDivElement | null>(null)
  const dispatch = useProjectDispatch()
  const courseState = useProjectContext(getCourseState)
  const sections = useProjectContext(getSections)
  const endCourseButton = useProjectContext(getCourseEndButtonElement)
  const progress = useProjectContext(getProgress, section?.id)
  const font = useFontCss(DeviceMode.desktop)

  const ids =
    courseState.incompleteElements
      ?.filter((element) => element.sectionId === section?.id)
      .map((el) => el.elementId) || []

  const emptyOtherSections = progress === 100 && courseState.viewNotCompletedSections

  const finishButtonEnable =
    courseState.notCompletedSections?.length === 0 && endCourseButton.sectionId !== section?.id

  const elements = useProjectContext(getAllElementsByIds, {
    ids,
  })

  const onClose = (e: React.MouseEvent | KeyboardEvent) => {
    e.stopPropagation()
    dispatch(hideIncompleteElementList())
    dispatch(hideNotCompletedSections())
  }

  const handleNavClick = (e: React.MouseEvent, elementId: string) => {
    e.stopPropagation()
    e.preventDefault()
    const el = courseState.incompleteElements?.find((el) => el.elementId === elementId)
    if (el) {
      focusFirstFocusableInElement(el.elementId)
      dispatch(setHighlightPreview(elementId))
      dispatch(
        setElementNavigation({
          sectionId: el.sectionId,
          elementId: el.elementId,
          nodeId: el.nodeId,
          blockId: el.blockId,
        }),
      )
    }
  }

  const goToIncompleteSections = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    const nextSection = courseState.incompleteElements?.filter(
      (el) => el.sectionId !== section?.id,
    )[0]
    const notCompletedSectionId = courseState.notCompletedSections?.[0]
    const notCompletedSection = sections.find((s) => s.id === notCompletedSectionId)

    if (nextSection || notCompletedSection) {
      if (notCompletedSection?.type === SectionTypeEnum.test) {
        dispatch(
          setCourseNavigation({
            sectionId: notCompletedSectionId,
            blockId: notCompletedSection?.blocksOrder[0],
            fromSectionId: section?.id,
            fromSectionName: section?.name,
            fromSectionType: section?.type,
          }),
        )
      } else {
        dispatch(
          setCourseNavigation(
            nextSection || {
              sectionId: notCompletedSectionId,
              fromSectionId: section?.id,
              fromSectionName: section?.name,
              fromSectionType: section?.type,
            },
          ),
        )
      }
    } else if (finishButtonEnable) {
      dispatch(
        setElementNavigation({
          sectionId: endCourseButton.sectionId,
          elementId: endCourseButton?.element?.id,
          nodeId: endCourseButton.nodeId,
          blockId: endCourseButton?.block?.uuid,
        }),
      )
    }
  }

  useEffect(() => {
    if (!courseState.incompleteElements?.length) {
      dispatch(hideIncompleteElementList())
    }
    if (
      courseState.notCompletedSections?.length === 0 &&
      endCourseButton.sectionId === section?.id
    ) {
      dispatch(hideNotCompletedSections())
    }
  }, [
    courseState.incompleteElements,
    courseState.notCompletedSections?.length,
    endCourseButton,
    section,
    dispatch,
  ])

  const emptyText =
    courseState.notCompletedSections?.length === 0
      ? pt('incompleteCourseModal.emptyAllCompleted')
      : emptyOtherSections
        ? pt('incompleteCourseModal.emptyNextSection')
        : pt('incompleteCourseModal.emptyCurrentSection')

  const incompleteHeaderId = 'incomplete-elements-header'
  const descriptionId = 'incomplete-elements-description'
  const Header = (
    <h2
      className={s.header}
      id={incompleteHeaderId}
      style={{ fontFamily: font.base.fontFamily, color: font.base.color }}
    >
      {pt('incompleteCourseModal.title')}
    </h2>
  )

  const showModal = courseState.incompletePopoverVisible || courseState.viewNotCompletedSections

  useEffect(() => {
    if (showModal && refRoot.current) {
      focusFirstFocusableInElement(refRoot.current)
    }
  }, [showModal, courseState.incompleteElementShakeKey])

  return (
    <AnimatePresence>
      {showModal && (
        <DraggablePanel
          ariaDescribedBy={descriptionId}
          ariaLabelledBy={incompleteHeaderId}
          header={Header}
          onClose={onClose}
          refRoot={refRoot}
          shakeKey={courseState.incompleteElementShakeKey}
        >
          {!!ids.length && !courseState.viewNotCompletedSections && (
            <span
              className={s.hint}
              id={descriptionId}
              style={{ fontFamily: font.base.fontFamily }}
            >
              {pt('incompleteCourseModal.hint')}
            </span>
          )}
          <div
            className={cn(s.elements, { [s.empty]: emptyOtherSections })}
            style={{ fontFamily: font.base.fontFamily }}
          >
            <AnimatePresence>
              {elements.length ? (
                <IncompleteElementsListStyled>
                  {elements.map(({ element }) => {
                    const label = getElementLabel(element.type)
                    return (
                      <li key={element.id}>
                        <IncompleteElementButton
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0, height: 0 }}
                          onClick={(e) => handleNavClick(e, element.id)}
                          transition={{ duration: 0.3 }}
                          layout
                        >
                          {t(label)}
                        </IncompleteElementButton>
                      </li>
                    )
                  })}
                </IncompleteElementsListStyled>
              ) : (
                <div className={s.empty}>
                  <Empty size={CourseKitSize.XS} text={emptyText} grow />
                  {emptyOtherSections && (
                    <ButtonCourse
                      name='incompleteSections'
                      onClick={goToIncompleteSections}
                      size={CourseKitSize.XS}
                    >
                      {finishButtonEnable
                        ? pt('incompleteCourseModal.finalButton')
                        : pt('incompleteCourseModal.nextButton')}
                    </ButtonCourse>
                  )}
                </div>
              )}
            </AnimatePresence>
          </div>
        </DraggablePanel>
      )}
    </AnimatePresence>
  )
}

export default IncompleteElementModal
